import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { Expense } from 'src/app/models/Answer.model';
import { QueDatePipe } from 'src/app/pipes/date.pipe';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { ApprovalStore } from '../../store/approval-store';

@Component({
    selector: 'que-approval-expense-row',
    imports: [CommonModule, TranslateDirective, TranslatePipe, QueDatePipe, ToggleComponent],
    templateUrl: './approval-expense-row.component.html',
    styleUrls: ['./approval-expense-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalExpenseRowComponent implements OnDestroy {
  @Input() row!: Expense;
  @Input() canEdit = false;
  @Input() approvalId?: number;
  @Output() rowClick = new EventEmitter<void>();

  #approvalStore = inject(ApprovalStore);

  approvedAmountError = false;

  ngOnDestroy(): void {
    // Nothing invalid ever reaches the server (saveApprovedAmount refuses to send it), so an
    // in-progress, unsaved error dies with the row - don't leave bulkApprove blocked on a row
    // that's no longer even on screen to fix.
    if (this.approvedAmountError && this.approvalId) {
      this.#approvalStore.setApprovalRowValidity({ approvalId: this.approvalId, isInvalid: false });
    }
  }

  // A freshly-submitted row always stores ApprovedAmount as 0, never null (see
  // ApprovalsService.UpdateApprovalStatus), so an untouched row must be detected by
  // value, not by a null-check - hence `||` here rather than `??`.
  get displayApprovedAmount(): number | null {
    return this.row.approvedAmount || this.row.amount;
  }

  openFile(file: { guid: string | null }, ev: Event): void {
    ev.stopPropagation();
    if (file.guid) {
      window.open(`/viewFile/${file.guid}`, '_blank');
    }
  }

  toggleClientCharge(state: boolean): void {
    if (!this.canEdit || !this.approvalId) return;
    this.#approvalStore.updateExpenseField({
      approvalId: this.approvalId,
      expenseId: this.row.id,
      fields: { clientCharge: state }
    });
  }

  setFullcharge(isFull: boolean, ev: Event): void {
    ev.stopPropagation();
    if (!this.canEdit || !this.approvalId) return;
    this.#approvalStore.updateExpenseField({
      approvalId: this.approvalId,
      expenseId: this.row.id,
      fields: { fullcharge: isFull }
    });
  }

  saveClientChargeAmount(ev: Event): void {
    ev.stopPropagation();
    if (!this.canEdit || !this.approvalId) return;
    const input = ev.target as HTMLInputElement;
    const value = input.value === '' ? null : Number(input.value);
    this.#approvalStore.updateExpenseField({
      approvalId: this.approvalId,
      expenseId: this.row.id,
      fields: { clientChargeAmount: value }
    });
  }

  saveApprovedAmount(ev: Event): void {
    ev.stopPropagation();
    if (!this.canEdit || !this.approvalId) return;
    const input = ev.target as HTMLInputElement;
    const value = input.value === '' ? null : Number(input.value);

    if (value !== null && this.row.amount !== null && value > this.row.amount) {
      this.approvedAmountError = true;
      if (this.approvalId) this.#approvalStore.setApprovalRowValidity({ approvalId: this.approvalId, isInvalid: true });
      return;
    }
    if (this.approvedAmountError && this.approvalId) {
      this.#approvalStore.setApprovalRowValidity({ approvalId: this.approvalId, isInvalid: false });
    }
    this.approvedAmountError = false;

    this.#approvalStore.updateExpenseField({
      approvalId: this.approvalId,
      expenseId: this.row.id,
      fields: { approvedAmount: value }
    });
  }

  saveComment(ev: Event): void {
    ev.stopPropagation();
    if (!this.canEdit || !this.approvalId) return;
    const input = ev.target as HTMLInputElement;
    this.#approvalStore.updateExpenseField({
      approvalId: this.approvalId,
      expenseId: this.row.id,
      fields: { commentAproval: input.value }
    });
  }

  onRowClick(): void {
    this.rowClick.emit();
  }
}
